import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Clock, Delete, LogIn, LogOut, Check, Scissors, ChevronLeft, Maximize, Minimize, Shield, X, Settings as SettingsIcon, Plane, Thermometer, User as UserIcon, FileEdit, Calendar as CalIcon, Send, Sparkles } from "lucide-react";
import { useBranding } from "../contexts/BrandingContext";
import useKioskManifest from "../hooks/useKioskManifest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIOSK_CODE_STORAGE_KEY = "salonflow_kiosk_code";

// Curated affirmations rotated deterministically by day of year so every
// stylist on the team sees the same one when they clock in. We seed picks
// from day-of-year so a long list (60+) doesn't repeat in a season but is
// still totally consistent across devices/refreshes within the same day.
const AFFIRMATIONS = [
  "You are exactly the kind of stylist someone would brag about.",
  "Today, your hands tell stories. Make them beautiful ones.",
  "Confidence walks in with you, and clients feel it.",
  "Every chair you turn is someone's better day.",
  "Your craft is rare. Don't ever forget that.",
  "Be kind to yourself first. Everything else follows.",
  "You're allowed to take up space. So is your art.",
  "Slow hands. Steady mind. Beautiful work.",
  "The best stylists notice details. Today, notice yours.",
  "You bring more than skill — you bring care.",
  "Keep showing up. Magic compounds.",
  "Your work changes how people see themselves. That matters.",
  "Trust the process — and trust the version of you doing it.",
  "Some days are art. Some days are practice. Both build mastery.",
  "Your energy is contagious. Choose it well.",
  "Done is better than perfect — and yours is already great.",
  "Today, lead with your eyes and your ears, not your scissors.",
  "The chair behind you is empty for a reason. You earn it.",
  "Small wins, all day. That's what builds a great career.",
  "You are not behind. You are exactly where the work is.",
  "Pour into yourself today like you pour into them.",
  "Compliment yourself out loud. You'd say it to a friend.",
  "Stay curious. The good ones never stop learning.",
  "Be the energy you want behind your chair.",
  "Quality, not speed. Speed comes from quality.",
  "Your boundaries are also your branding.",
  "Take the deep breath. Then go change someone's day.",
  "Be the stylist you needed when you were starting out.",
  "Clients can feel a calm soul. Be calm first.",
  "Your reputation is built one kindness at a time.",
  "Honesty looks better than any blowout.",
  "You don't have to be perfect to be amazing.",
  "Make eye contact. Make memories. Make money.",
  "Today, hydrate, breathe, listen, create. In that order.",
  "Compete with yesterday's you — no one else.",
  "Stand a little taller. They're watching, and they should.",
  "You are a master in motion. Don't dim it.",
  "The right people are coming. Keep the door open.",
  "Your presence is part of the service.",
  "Beauty starts before the first comb-out — with you.",
  "Hard days are part of the resume. Keep going.",
  "Speak to clients the way you'd want to be spoken to.",
  "You're not for everyone, and that's the point.",
  "When in doubt, listen longer.",
  "You're allowed to charge what you're worth.",
  "A clean station is a clear mind.",
  "Today, be the calm in the storm.",
  "Confidence is a choice. Make it now.",
  "You belong here. The chair fits you.",
  "There is no rush. There is only craft.",
  "Be soft. Be skilled. Be sharp.",
  "Tip the universe with a great attitude.",
  "Every great career has a Tuesday. This is yours.",
  "Smile at the mirror first. The clients will follow.",
  "Be excellent to your future self.",
  "Make it look easy. They don't need to see the work.",
  "Your craft outlives the trend.",
  "Be brave enough to be a beginner — even today.",
  "Set the tone. Don't catch the tone.",
  "You're allowed to want more, and to ask for it.",
];

const dayOfYear = (d) => {
  // 1..366 — used as a deterministic index into AFFIRMATIONS.
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start + (start.getTimezoneOffset() - d.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / 86400000);
};
const getDailyAffirmation = (date = new Date()) =>
  AFFIRMATIONS[dayOfYear(date) % AFFIRMATIONS.length];

const formatTime = (iso, tz) => new Date(iso).toLocaleTimeString("en-US", {
  hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz || undefined,
});
const formatHoursMinutes = (dec) => {
  const totalMinutes = Math.max(0, Math.round((dec || 0) * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// 3-stage kiosk: STAFF_GRID → PIN_ENTRY → CONFIRMATION (auto-return after 4s)
// Plus a parallel "request" flow: STAFF_GRID → PIN_ENTRY (intent='request') → REQUEST_PICK → REQUEST_FORM → REQUEST_CONFIRM
export default function ClockKioskPage() {
  useKioskManifest({ manifest: "/manifest-clock.json", title: "Time Clock" });
  const branding = useBranding();
  const [stage, setStage] = useState("grid"); // grid | pin | location | confirm | request_pick | request_form_time_off | request_form_missed_punch | request_confirm
  const [intent, setIntent] = useState("clock"); // 'clock' | 'request'
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [clockedInIds, setClockedInIds] = useState(new Set());
  const [onBreakIds, setOnBreakIds] = useState(new Set());
  const [locationOptions, setLocationOptions] = useState([]); // [{location_id, name, color}]
  const [savedPin, setSavedPin] = useState(""); // PIN cached between stages
  const [pendingCounts, setPendingCounts] = useState({}); // {user_id: count}
  // Form state for time-off / missed-punch (filled after PIN is verified)
  const [reqType, setReqType] = useState(""); // 'time_off' | 'missed_punch'
  const [toForm, setToForm] = useState({ type: "vacation", start_date: "", end_date: "", reason: "" });
  const [mpForm, setMpForm] = useState({ date: "", clock_in: "", clock_out: "", reason: "" });
  const [clockTick, setClockTick] = useState(new Date());
  const [paired, setPaired] = useState(() => !!localStorage.getItem(KIOSK_CODE_STORAGE_KEY));
  const [pairingInput, setPairingInput] = useState("");
  const [pairingError, setPairingError] = useState("");
  const [pairingSubmitting, setPairingSubmitting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [unpairOpen, setUnpairOpen] = useState(false);
  const [unpairInput, setUnpairInput] = useState("");
  const [unpairError, setUnpairError] = useState("");
  // Tenant timezone — fetched from the backend so the kiosk shows
  // salon-local time regardless of how the iPad is configured. Falls
  // back to the iPad's own zone only if we never got a response.
  const [tenantTz, setTenantTz] = useState(() => localStorage.getItem("kiosk_tenant_tz") || "");

  // Track fullscreen state
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* ignore */
    }
  };

  const verifyPairing = async (code) => {
    setPairingSubmitting(true);
    setPairingError("");
    try {
      const res = await axios.post(`${API}/kiosk/verify-code`, { code });
      localStorage.setItem(KIOSK_CODE_STORAGE_KEY, code);
      // Cache the salon's timezone so the kiosk header / time displays
      // use it instead of trusting the iPad's local clock.
      const tz = res.data?.timezone || "America/New_York";
      localStorage.setItem("kiosk_tenant_tz", tz);
      setTenantTz(tz);
      setPaired(true);
    } catch (err) {
      setPairingError(err.response?.data?.detail || "Invalid code. Ask an owner or manager to check the pairing code in Time Clock.");
    }
    setPairingSubmitting(false);
  };

  const handleUnpair = async () => {
    const stored = localStorage.getItem(KIOSK_CODE_STORAGE_KEY);
    if (unpairInput.trim() === stored) {
      localStorage.removeItem(KIOSK_CODE_STORAGE_KEY);
      setPaired(false);
      setUnpairOpen(false);
      setUnpairInput("");
      setUnpairError("");
    } else {
      setUnpairError("Code doesn't match.");
    }
  };

  // Live clock header
  useEffect(() => {
    const id = setInterval(() => setClockTick(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Refresh the cached tenant timezone on every kiosk mount so iPads
  // that paired before this feature shipped pick up the salon's correct
  // zone instead of showing UTC / device-local time.
  useEffect(() => {
    if (!paired) return;
    const stored = localStorage.getItem(KIOSK_CODE_STORAGE_KEY);
    if (!stored) return;
    axios.post(`${API}/kiosk/verify-code`, { code: stored })
      .then(res => {
        const tz = res.data?.timezone;
        if (!tz) return;
        localStorage.setItem("kiosk_tenant_tz", tz);
        setTenantTz(tz);
      })
      .catch(() => { /* stale code will be caught later when staff fetch fails */ });
  }, [paired]);

  const loadStaff = useCallback(async () => {
    if (!paired) return;
    try {
      const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
      const [staffRes, summaryRes, countsRes] = await Promise.all([
        axios.get(`${API}/employee-pins/staff`, { headers: { "X-Kiosk-Code": kioskCode } }),
        axios.get(`${API}/timeclock/summary`).catch(() => ({ data: [] })),
        axios.get(`${API}/kiosk/pending-counts`, { headers: { "X-Kiosk-Code": kioskCode } }).catch(() => ({ data: {} })),
      ]);
      // Filter out staff without a PIN — they can't clock in/out anyway,
      // so they shouldn't take up screen real estate on the kiosk grid.
      // Managers set PINs from Staff → Set PIN; once set, the card appears.
      const onlyWithPin = (staffRes.data || []).filter(s => s.has_pin === true);
      setStaff(onlyWithPin);
      // Build clocked-in / on-break sets directly from the enriched
      // /employee-pins/staff response — falls back to /timeclock/summary
      // for redundancy in case one of the two endpoints is slow/stale.
      const inIds = new Set();
      const breakIds = new Set();
      for (const s of (staffRes.data || [])) {
        if (s.clocked_in) inIds.add(s.user_id);
        if (s.on_break) breakIds.add(s.user_id);
      }
      for (const s of (summaryRes.data || [])) {
        if (s.clocked_in) inIds.add(s.user_id);
        if (s.on_break) breakIds.add(s.user_id);
      }
      setClockedInIds(inIds);
      setOnBreakIds(breakIds);
      setPendingCounts(countsRes.data || {});
    } catch {
      setStaff([]);
    }
  }, [paired]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  // Auto-return from confirmation → grid. Default 4s; when the punch
  // surfaced one or more time-off decisions, we give the employee a
  // little longer to read them (and a "Got it" button to ack early).
  useEffect(() => {
    if (stage !== "confirm" && stage !== "request_confirm") return;
    const hasNotifs = (result?.time_off_notifications || []).length > 0;
    const id = setTimeout(() => {
      // Fire-and-forget ack of any time-off decisions the employee just
      // saw. Done on auto-return so they don't see the same banner twice.
      if (hasNotifs && result?._user_id && result?._pin) {
        const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
        axios.post(`${API}/timeclock/kiosk-ack-time-off`, {
          user_id: result._user_id,
          pin: result._pin,
          request_ids: result.time_off_notifications.map(n => n.request_id),
        }, { headers: { "X-Kiosk-Code": kioskCode } }).catch(() => {});
      }
      setStage("grid");
      setIntent("clock");
      setSelected(null);
      setPin("");
      setError("");
      setResult(null);
      setReqType("");
      setSavedPin("");
      loadStaff();
    }, hasNotifs ? 10000 : 4000);
    return () => clearTimeout(id);
  }, [stage, result, loadStaff]);

  // 'clock' (default toggle), 'break' (start/end a break inside an open shift)
  const [kioskAction, setKioskAction] = useState("clock");

  const selectStaff = (s) => {
    setSelected(s);
    setPin("");
    setError("");
    setKioskAction("clock");
    setStage("pin");
  };

  const pressKey = (digit) => {
    setError("");
    if (pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 4) {
      // Branch: clock-in/out auto-submits; request flow stashes the PIN and moves on
      if (intent === "request") {
        setSavedPin(next);
        setPin("");
        setStage("request_pick");
      } else if (kioskAction === "break") {
        submitBreak(next);
      } else {
        submitPin(next);
      }
    }
  };

  const backspace = () => {
    setError("");
    setPin(p => p.slice(0, -1));
  };

  const submitBreak = async (fullPin) => {
    setSubmitting(true);
    setError("");
    try {
      const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
      const res = await axios.post(`${API}/timeclock/kiosk-break`,
        { user_id: selected.user_id, pin: fullPin },
        { headers: { "X-Kiosk-Code": kioskCode } });
      setResult({ ...res.data, _pin: fullPin, _user_id: selected.user_id });
      setStage("confirm");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(KIOSK_CODE_STORAGE_KEY);
        setPaired(false);
        setPin("");
        setSubmitting(false);
        return;
      }
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong");
      setError(msg);
      setPin("");
    }
    setSubmitting(false);
  };

  const submitPin = async (fullPin, locationId = null) => {
    setSubmitting(true);
    setError("");
    try {
      const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
      const payload = { user_id: selected.user_id, pin: fullPin };
      if (locationId) payload.location_id = locationId;
      const res = await axios.post(`${API}/timeclock/kiosk-toggle`, payload, {
        headers: { "X-Kiosk-Code": kioskCode },
      });
      setResult({ ...res.data, _pin: fullPin, _user_id: selected.user_id });
      setStage("confirm");
      setSavedPin("");
      setLocationOptions([]);
    } catch (err) {
      if (err.response?.status === 401) {
        // Unpaired or code changed — force re-pairing
        localStorage.removeItem(KIOSK_CODE_STORAGE_KEY);
        setPaired(false);
        setPin("");
        setSavedPin("");
        setSubmitting(false);
        return;
      }
      if (err.response?.status === 409 && err.response?.data?.detail?.code === "LOCATION_REQUIRED") {
        // Backend needs the user to pick a location. Show the picker.
        setSavedPin(fullPin);
        setLocationOptions(err.response.data.detail.locations || []);
        setStage("location");
        setSubmitting(false);
        return;
      }
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong");
      setError(msg);
      setPin("");
    }
    setSubmitting(false);
  };

  const chooseLocation = (loc) => {
    if (!savedPin) return;
    submitPin(savedPin, loc.location_id);
  };

  const backToGrid = () => {
    setStage("grid");
    setIntent("clock");
    setSelected(null);
    setPin("");
    setError("");
    setSavedPin("");
    setLocationOptions([]);
    setReqType("");
    setToForm({ type: "vacation", start_date: "", end_date: "", reason: "" });
    setMpForm({ date: "", clock_in: "", clock_out: "", reason: "" });
  };

  // Computes the local-to-the-salon YYYY-MM-DD. Critical: an iPad
  // sitting in UTC at 8pm EST would otherwise report tomorrow's date
  // for the "today" date picker on missed-punch requests.
  const todayISO = () => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tenantTz || undefined, year: "numeric", month: "2-digit", day: "2-digit",
    });
    return fmt.format(new Date());
  };

  const startRequestFlow = () => {
    setIntent("request");
    setStage("grid");
  };

  const submitTimeOff = async () => {
    if (!toForm.start_date || !toForm.end_date) {
      setError("Pick start and end dates"); return;
    }
    if (toForm.end_date < toForm.start_date) {
      setError("End date must be on or after start date"); return;
    }
    setSubmitting(true);
    setError("");
    try {
      const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
      const res = await axios.post(`${API}/kiosk/time-off`, {
        user_id: selected.user_id,
        pin: savedPin,
        type: toForm.type,
        start_date: toForm.start_date,
        end_date: toForm.end_date,
        reason: toForm.reason.trim(),
      }, { headers: { "X-Kiosk-Code": kioskCode } });
      setResult({ kind: "time_off", ...res.data });
      setStage("request_confirm");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(KIOSK_CODE_STORAGE_KEY);
        setPaired(false);
      } else if (err.response?.status === 403) {
        // PIN was wrong — bounce back to PIN entry
        setError("Incorrect PIN. Please try again.");
        setSavedPin("");
        setStage("pin");
      } else {
        setError(err.response?.data?.detail || "Could not submit");
      }
    }
    setSubmitting(false);
  };

  const submitMissedPunch = async () => {
    if (!mpForm.date || !mpForm.clock_in) {
      setError("Pick the date and clock-in time"); return;
    }
    const startIso = `${mpForm.date}T${mpForm.clock_in}:00`;
    const endIso = mpForm.clock_out ? `${mpForm.date}T${mpForm.clock_out}:00` : null;
    setSubmitting(true);
    setError("");
    try {
      const kioskCode = localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
      const res = await axios.post(`${API}/kiosk/missed-punch`, {
        user_id: selected.user_id,
        pin: savedPin,
        requested_clock_in: startIso,
        requested_clock_out: endIso,
        reason: mpForm.reason.trim(),
      }, { headers: { "X-Kiosk-Code": kioskCode } });
      setResult({ kind: "missed_punch", ...res.data });
      setStage("request_confirm");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(KIOSK_CODE_STORAGE_KEY);
        setPaired(false);
      } else if (err.response?.status === 403) {
        setError("Incorrect PIN. Please try again.");
        setSavedPin("");
        setStage("pin");
      } else {
        setError(err.response?.data?.detail || "Could not submit");
      }
    }
    setSubmitting(false);
  };

  const logoSrc = branding.getLogoSrc?.();

  // ========= Pairing gate =========
  if (!paired) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 flex items-center justify-center" data-testid="clock-kiosk-pair-screen">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-rose-500/10 blur-3xl" />
          <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-violet-500/10 blur-3xl" />
        </div>
        <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-10 max-w-md w-full mx-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white mb-2">Pair this device</h1>
            <p className="text-white/50 font-['Manrope'] mb-8 leading-relaxed">
              Ask an owner or manager for the 6-digit pairing code. It's visible on the Time Clock page under "Kiosk Pairing".
            </p>
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              value={pairingInput}
              onChange={e => { setPairingError(""); setPairingInput(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
              placeholder="000000"
              data-testid="kiosk-pair-input"
              className="w-full h-16 text-center bg-white/10 border border-white/10 rounded-2xl text-white text-3xl tracking-[0.4em] font-mono font-semibold placeholder-white/20 focus:outline-none focus:border-rose-400/40 focus:bg-white/15 tabular-nums"
            />
            {pairingError && (
              <p className="text-rose-400 text-sm mt-3 font-['Manrope']" data-testid="kiosk-pair-error">{pairingError}</p>
            )}
            <button
              onClick={() => verifyPairing(pairingInput)}
              disabled={pairingInput.length !== 6 || pairingSubmitting}
              data-testid="kiosk-pair-submit"
              className="w-full mt-6 h-12 rounded-full bg-white text-slate-900 font-semibold font-['Outfit'] hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {pairingSubmitting ? "Verifying…" : "Pair Device"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Kiosk page uses normal document flow (not `position: fixed`) so iOS
    // Safari touch-scroll works on iPad. Fixed positioning + overflow-auto
    // is a known broken combo on iOS — momentum scrolling fails and
    // sometimes scrolling doesn't initiate at all.
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 overflow-x-hidden relative" data-testid="clock-kiosk-page">
      {/* Decorative glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-rose-500/10 blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-teal-500/10 blur-3xl" />
      </div>

      {/* Header bar */}
      <div className="relative px-10 py-7 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {logoSrc ? (
            <img src={logoSrc} alt="logo" className="h-9 w-auto" />
          ) : (
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
          )}
          <div>
            <p className="text-white/90 text-sm font-semibold font-['Outfit'] tracking-tight">{branding.app_name || "Salon"}</p>
            <p className="text-white/40 text-[11px] font-['Manrope'] uppercase tracking-[0.18em] font-semibold">Time Clock Kiosk</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-white font-['Outfit'] text-2xl font-semibold tabular-nums tracking-tight" data-testid="kiosk-clock">
              {clockTick.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tenantTz || undefined })}
            </p>
            <p className="text-white/40 text-xs font-['Manrope'] mt-0.5">
              {clockTick.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: tenantTz || undefined })}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={toggleFullscreen} data-testid="kiosk-fullscreen-toggle"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen lock"}
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition-all">
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>
            <button onClick={() => setUnpairOpen(true)} data-testid="kiosk-settings-btn"
              title="Unpair device"
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/50 hover:text-white/80 flex items-center justify-center transition-all">
              <SettingsIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-10 pb-10">
        {/* Stage: GRID */}
        {stage === "grid" && (
          <div data-testid="kiosk-grid">
            {/* Affirmation of the Day — picks deterministically by day-of-year
                so every stylist sees the same one all day. Hidden on the
                request-flow heading because that flow already has its own
                copy and we don't want to push the staff grid below the fold. */}
            {intent !== "request" && (
              <div className="mb-8 mx-auto max-w-3xl" data-testid="kiosk-affirmation">
                <div className="relative px-6 py-5 rounded-2xl bg-gradient-to-r from-rose-500/10 via-violet-500/10 to-teal-500/10 border border-white/10 backdrop-blur-sm overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
                  <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 via-violet-400 to-teal-400 flex items-center justify-center shadow-lg shadow-rose-500/20">
                      <Sparkles className="w-5 h-5 text-white" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 font-['Manrope'] mb-1.5">
                        Affirmation of the day
                      </p>
                      <p className="text-white/90 text-lg font-['Outfit'] font-light leading-snug" data-testid="affirmation-text">
                        {getDailyAffirmation()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="mb-10 text-center">
              {intent === "request" ? (
                <>
                  <h1 className="font-['Outfit'] text-5xl font-semibold tracking-tight text-white mb-3">Tap your name to submit a request.</h1>
                  <p className="text-white/50 font-['Manrope'] text-lg">Time off or a missed clock-in. Use your PIN to confirm.</p>
                </>
              ) : (
                <>
                  <h1 className="font-['Outfit'] text-5xl font-semibold tracking-tight text-white mb-3">Tap your name to clock in or out.</h1>
                  <p className="text-white/50 font-['Manrope'] text-lg">Use your 4-digit PIN to confirm.</p>
                </>
              )}
            </div>
            {staff.length === 0 ? (
              <p className="text-center text-white/50 font-['Manrope'] mt-20">No staff with PINs set up yet. Ask a manager to set PINs for the team.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {staff.map(s => {
                  const inShift = clockedInIds.has(s.user_id);
                  const onBreak = onBreakIds.has(s.user_id);
                  const pendingN = pendingCounts[s.user_id] || 0;
                  const noPin = s.has_pin === false;
                  return (
                    <button key={s.user_id}
                      onClick={() => noPin
                        ? setError(`${s.name?.split(" ")[0] || "This person"} needs an employee PIN set first. Ask a manager to set it from Staff → Set PIN.`)
                        : selectStaff(s)}
                      data-testid={`kiosk-staff-${s.user_id}`}
                      className={`group relative aspect-[3/4] rounded-3xl flex flex-col items-center justify-center backdrop-blur transition-all overflow-hidden ${
                        noPin ? "bg-white/[0.02] border border-dashed border-white/15 opacity-60 hover:opacity-80 cursor-help"
                          : onBreak ? "bg-white/5 hover:bg-white/10 border-2 border-amber-400/60 ring-2 ring-amber-400/20"
                          : inShift ? "bg-white/5 hover:bg-white/10 border-2 border-emerald-400/40 ring-1 ring-emerald-400/10"
                          : "bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20"
                      }`}
                    >
                      {/* Subtle aura glow at the bottom of the card to make
                          state instantly readable from across the room. */}
                      {onBreak && (
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-amber-500/20 to-transparent pointer-events-none" />
                      )}
                      {inShift && !onBreak && intent === "clock" && (
                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-emerald-500/15 to-transparent pointer-events-none" />
                      )}
                      {/* State chip — top right */}
                      {noPin && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/10 border border-white/15"
                          data-testid={`kiosk-no-pin-chip-${s.user_id}`}>
                          <span className="text-[9px] font-bold text-white/70 uppercase tracking-widest font-['Manrope']">PIN required</span>
                        </div>
                      )}
                      {!noPin && onBreak && intent === "clock" && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-400/95 flex items-center gap-1" data-testid={`kiosk-on-break-chip-${s.user_id}`}>
                          <div className="w-1.5 h-1.5 bg-amber-900 rounded-full animate-pulse" />
                          <span className="text-[9px] font-bold text-amber-950 uppercase tracking-widest font-['Manrope']">On Break</span>
                        </div>
                      )}
                      {!noPin && inShift && !onBreak && intent === "clock" && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-emerald-500/90 flex items-center gap-1" data-testid={`kiosk-on-clock-chip-${s.user_id}`}>
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          <span className="text-[9px] font-bold text-white uppercase tracking-widest font-['Manrope']">On Clock</span>
                        </div>
                      )}
                      {pendingN > 0 && !noPin && (
                        <div
                          data-testid={`kiosk-pending-badge-${s.user_id}`}
                          title={`${pendingN} request${pendingN === 1 ? "" : "s"} pending review`}
                          className="absolute top-3 left-3 min-w-[1.5rem] h-6 px-1.5 rounded-full bg-amber-400/90 border border-amber-200/60 flex items-center justify-center gap-1">
                          <span className="text-[9px] font-bold text-amber-950 uppercase tracking-wider font-['Manrope']">{pendingN}</span>
                          <span className="text-[8px] font-bold text-amber-950/80 uppercase tracking-widest font-['Manrope']">pending</span>
                        </div>
                      )}
                      <div className={`relative w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold font-['Outfit'] shadow-lg ${noPin ? "grayscale" : ""}`}
                        style={{ background: `linear-gradient(135deg, ${s.color || "#F43F5E"}, ${s.color || "#F43F5E"}aa)` }}>
                        {s.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
                      </div>
                      <p className="relative mt-5 text-white font-['Outfit'] font-semibold text-lg">{s.name}</p>
                      <p className="relative text-white/40 text-xs font-['Manrope'] capitalize">{(s.title || s.role || "").replace("_", " ")}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Submit-a-request entry point — only on the default clock grid */}
            {intent === "clock" && staff.length > 0 && (
              <div className="mt-10 flex justify-center">
                <button onClick={startRequestFlow}
                  data-testid="kiosk-start-request-flow"
                  className="group inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white text-sm font-['Manrope'] font-semibold transition-all">
                  <FileEdit className="w-4 h-4" />
                  Submit a request — time off or missed clock-in
                </button>
              </div>
            )}
            {intent === "request" && (
              <div className="mt-10 flex justify-center">
                <button onClick={backToGrid}
                  data-testid="kiosk-cancel-request-flow"
                  className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-sm font-['Manrope'] transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back to clock-in
                </button>
              </div>
            )}
          </div>
        )}

        {/* Stage: PIN */}
        {stage === "pin" && selected && (
          <div className="max-w-md mx-auto mt-8" data-testid="kiosk-pin">
            <button onClick={backToGrid} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']" data-testid="kiosk-back-btn">
              <ChevronLeft className="w-4 h-4" />Back
            </button>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-8">
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold font-['Outfit'] shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${selected.color || "#F43F5E"}, ${selected.color || "#F43F5E"}aa)` }}>
                  {selected.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
                </div>
                <p className="mt-5 text-white font-['Outfit'] text-2xl font-semibold">Hi {selected.name.split(" ")[0]}</p>
                <p className="text-white/50 text-sm mt-1 font-['Manrope']">
                  {(() => {
                    const onBreak = onBreakIds.has(selected.user_id);
                    const inShift = clockedInIds.has(selected.user_id);
                    if (kioskAction === "break" && onBreak) return "Enter your PIN to end your break";
                    if (kioskAction === "break") return "Enter your PIN to start your break";
                    if (onBreak) return "Currently on break — enter PIN to clock out, or tap Break below";
                    if (inShift) return "Enter PIN to clock out, or tap Take Break below";
                    return "Enter your 4-digit PIN to clock in";
                  })()}
                </p>
              </div>

              {/* Action toggle — only for stylists who are currently clocked
                  in. Lets them split between "Clock Out" (default) and
                  "Take/End Break". Hidden during request flow. */}
              {intent === "clock" && clockedInIds.has(selected.user_id) && (
                <div className="grid grid-cols-2 gap-2 mt-6" data-testid="kiosk-action-toggle">
                  <button
                    onClick={() => { setKioskAction("clock"); setPin(""); setError(""); }}
                    data-testid="kiosk-action-clock"
                    className={`px-4 py-3 rounded-2xl text-sm font-semibold font-['Manrope'] transition-all ${
                      kioskAction === "clock"
                        ? "bg-white text-slate-900 shadow-lg"
                        : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                    }`}
                  >
                    <LogOut className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                    Clock Out
                  </button>
                  <button
                    onClick={() => { setKioskAction("break"); setPin(""); setError(""); }}
                    data-testid="kiosk-action-break"
                    className={`px-4 py-3 rounded-2xl text-sm font-semibold font-['Manrope'] transition-all ${
                      kioskAction === "break"
                        ? "bg-amber-400 text-amber-950 shadow-lg"
                        : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                    }`}
                  >
                    <Clock className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                    {onBreakIds.has(selected.user_id) ? "End Break" : "Take Break"}
                  </button>
                </div>
              )}

              {/* PIN dots */}
              <div className="flex items-center justify-center gap-3 mt-8 mb-2" data-testid="kiosk-pin-dots">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${
                    i < pin.length ? "bg-white scale-100" : "bg-white/15 scale-90"
                  }`} />
                ))}
              </div>

              {error && (
                <p className="text-rose-400 text-sm text-center mt-3 font-['Manrope']" data-testid="kiosk-pin-error">{error}</p>
              )}

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-3 mt-8">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                  <button key={n} onClick={() => pressKey(String(n))} disabled={submitting}
                    data-testid={`kiosk-key-${n}`}
                    className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/5 text-white text-2xl font-semibold font-['Outfit'] transition-colors disabled:opacity-50">
                    {n}
                  </button>
                ))}
                <div />
                <button onClick={() => pressKey("0")} disabled={submitting} data-testid="kiosk-key-0"
                  className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/5 text-white text-2xl font-semibold font-['Outfit'] transition-colors disabled:opacity-50">
                  0
                </button>
                <button onClick={backspace} disabled={submitting || pin.length === 0} data-testid="kiosk-key-del"
                  className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/5 text-white flex items-center justify-center transition-colors disabled:opacity-30">
                  <Delete className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage: LOCATION PICKER (multi-location staff) */}
        {stage === "location" && selected && (
          <div className="max-w-xl mx-auto" data-testid="kiosk-location">
            <button onClick={backToGrid} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']" data-testid="kiosk-loc-back">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="bg-white/5 border border-white/10 rounded-[32px] p-10 backdrop-blur-xl">
              <div className="flex flex-col items-center text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                  style={{ backgroundColor: selected.color || "#6B7280" }}>
                  <span className="text-white text-2xl font-bold font-['Outfit']">
                    {(selected.name || "?").trim().split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <h2 className="font-['Outfit'] text-3xl font-semibold text-white tracking-tight mb-2">
                  Which location, {selected.name?.split(" ")[0] || ""}?
                </h2>
                <p className="text-white/60 font-['Manrope'] text-sm mb-8">
                  Pick where you're working this shift
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                  {locationOptions.map(loc => (
                    <button
                      key={loc.location_id}
                      onClick={() => chooseLocation(loc)}
                      disabled={submitting}
                      data-testid={`kiosk-loc-${loc.location_id}`}
                      className="group relative overflow-hidden rounded-2xl p-5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/30 transition-all active:scale-[0.98] disabled:opacity-50">
                      <div className="absolute top-0 left-0 right-0 h-1"
                        style={{ backgroundColor: loc.color || "#EC4899" }} />
                      <p className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight">
                        {loc.name}
                      </p>
                      <p className="text-white/40 text-xs font-['Manrope'] mt-1">
                        Tap to clock in →
                      </p>
                    </button>
                  ))}
                </div>
                {error && (
                  <p className="text-rose-300 text-sm font-['Manrope'] mt-6" data-testid="kiosk-loc-error">{error}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stage: REQUEST PICK (after PIN, in 'request' intent) */}
        {stage === "request_pick" && selected && (
          <div className="max-w-2xl mx-auto mt-2" data-testid="kiosk-request-pick">
            <button onClick={backToGrid} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']" data-testid="kiosk-req-pick-back">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-10 text-center">
              <p className="font-['Outfit'] text-3xl font-semibold text-white tracking-tight mb-1">Hi {selected.name.split(" ")[0]}, what would you like to submit?</p>
              <p className="text-white/50 text-sm font-['Manrope'] mb-8">A manager will review and you'll get notified when it's approved.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button onClick={() => { setReqType("time_off"); setToForm({ type: "vacation", start_date: todayISO(), end_date: todayISO(), reason: "" }); setStage("request_form_time_off"); }}
                  data-testid="kiosk-pick-time-off"
                  className="group p-7 rounded-3xl bg-gradient-to-br from-sky-500/15 to-violet-500/10 border border-white/10 hover:border-white/30 transition-all text-left">
                  <div className="w-12 h-12 rounded-2xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center mb-4">
                    <Plane className="w-6 h-6 text-sky-200" strokeWidth={2} />
                  </div>
                  <p className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight">Time Off</p>
                  <p className="text-white/50 text-sm font-['Manrope'] mt-1">Vacation, sick, or personal day</p>
                </button>
                <button onClick={() => { setReqType("missed_punch"); setMpForm({ date: todayISO(), clock_in: "", clock_out: "", reason: "" }); setStage("request_form_missed_punch"); }}
                  data-testid="kiosk-pick-missed-punch"
                  className="group p-7 rounded-3xl bg-gradient-to-br from-amber-500/15 to-rose-500/10 border border-white/10 hover:border-white/30 transition-all text-left">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-amber-200" strokeWidth={2} />
                  </div>
                  <p className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight">Fix a Clock-In</p>
                  <p className="text-white/50 text-sm font-['Manrope'] mt-1">You forgot to clock in or out</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage: REQUEST_FORM_TIME_OFF */}
        {stage === "request_form_time_off" && selected && (
          <div className="max-w-xl mx-auto mt-2" data-testid="kiosk-form-time-off">
            <button onClick={() => setStage("request_pick")} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']" data-testid="kiosk-to-back">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center">
                  <Plane className="w-6 h-6 text-sky-200" />
                </div>
                <div>
                  <p className="font-['Outfit'] text-2xl font-semibold text-white">Request Time Off</p>
                  <p className="text-white/50 text-xs font-['Manrope']">{selected.name}</p>
                </div>
              </div>

              {/* Type picker */}
              <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Type</p>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { key: "vacation", label: "Vacation", Icon: Plane, color: "sky" },
                  { key: "sick",     label: "Sick",     Icon: Thermometer, color: "rose" },
                  { key: "personal", label: "Personal", Icon: UserIcon,   color: "violet" },
                ].map(({ key, label, Icon }) => {
                  const active = toForm.type === key;
                  return (
                    <button key={key} type="button"
                      onClick={() => setToForm(f => ({ ...f, type: key }))}
                      data-testid={`kiosk-to-type-${key}`}
                      className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition-all ${
                        active ? "bg-white/15 border-white/30 text-white" : "bg-white/5 border-white/10 text-white/50 hover:bg-white/10"
                      }`}>
                      <Icon className="w-5 h-5" strokeWidth={2} />
                      <span className="text-xs font-semibold">{label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div>
                  <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Start</p>
                  <input type="date" value={toForm.start_date}
                    data-testid="kiosk-to-start"
                    onChange={e => setToForm(f => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))}
                    className="w-full h-12 px-3 bg-white/10 border border-white/10 rounded-xl text-white font-['Manrope'] focus:outline-none focus:border-rose-400/40 [color-scheme:dark]" />
                </div>
                <div>
                  <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">End</p>
                  <input type="date" value={toForm.end_date} min={toForm.start_date}
                    data-testid="kiosk-to-end"
                    onChange={e => setToForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full h-12 px-3 bg-white/10 border border-white/10 rounded-xl text-white font-['Manrope'] focus:outline-none focus:border-rose-400/40 [color-scheme:dark]" />
                </div>
              </div>

              {/* Reason */}
              <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Reason (optional)</p>
              <textarea rows={2} value={toForm.reason}
                data-testid="kiosk-to-reason"
                onChange={e => setToForm(f => ({ ...f, reason: e.target.value.slice(0, 500) }))}
                placeholder="e.g. family trip"
                className="w-full px-3 py-2.5 bg-white/10 border border-white/10 rounded-xl text-white text-sm font-['Manrope'] placeholder-white/30 resize-none focus:outline-none focus:border-rose-400/40" />

              {error && <p className="text-rose-400 text-sm mt-3 font-['Manrope']" data-testid="kiosk-to-error">{error}</p>}

              <button onClick={submitTimeOff} disabled={submitting}
                data-testid="kiosk-to-submit"
                className="w-full mt-6 h-14 rounded-2xl bg-white text-slate-900 font-['Outfit'] font-semibold text-lg hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                {submitting ? "Sending…" : "Submit Request"}
              </button>
            </div>
          </div>
        )}

        {/* Stage: REQUEST_FORM_MISSED_PUNCH */}
        {stage === "request_form_missed_punch" && selected && (
          <div className="max-w-xl mx-auto mt-2" data-testid="kiosk-form-missed-punch">
            <button onClick={() => setStage("request_pick")} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']" data-testid="kiosk-mp-back">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-200" />
                </div>
                <div>
                  <p className="font-['Outfit'] text-2xl font-semibold text-white">Fix a Clock-In</p>
                  <p className="text-white/50 text-xs font-['Manrope']">{selected.name}</p>
                </div>
              </div>

              <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Date</p>
              <input type="date" value={mpForm.date} max={todayISO()}
                data-testid="kiosk-mp-date"
                onChange={e => setMpForm(f => ({ ...f, date: e.target.value }))}
                className="w-full h-12 px-3 bg-white/10 border border-white/10 rounded-xl text-white font-['Manrope'] mb-5 focus:outline-none focus:border-rose-400/40 [color-scheme:dark]" />

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div>
                  <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Clock-In</p>
                  <input type="time" value={mpForm.clock_in}
                    data-testid="kiosk-mp-in"
                    onChange={e => setMpForm(f => ({ ...f, clock_in: e.target.value }))}
                    className="w-full h-12 px-3 bg-white/10 border border-white/10 rounded-xl text-white font-['Manrope'] focus:outline-none focus:border-rose-400/40 [color-scheme:dark]" />
                </div>
                <div>
                  <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">Clock-Out (optional)</p>
                  <input type="time" value={mpForm.clock_out}
                    data-testid="kiosk-mp-out"
                    onChange={e => setMpForm(f => ({ ...f, clock_out: e.target.value }))}
                    className="w-full h-12 px-3 bg-white/10 border border-white/10 rounded-xl text-white font-['Manrope'] focus:outline-none focus:border-rose-400/40 [color-scheme:dark]" />
                </div>
              </div>

              <p className="text-white/60 text-xs font-['Manrope'] uppercase tracking-widest font-bold mb-2">What happened?</p>
              <textarea rows={2} value={mpForm.reason}
                data-testid="kiosk-mp-reason"
                onChange={e => setMpForm(f => ({ ...f, reason: e.target.value.slice(0, 500) }))}
                placeholder="e.g. forgot to clock in this morning"
                className="w-full px-3 py-2.5 bg-white/10 border border-white/10 rounded-xl text-white text-sm font-['Manrope'] placeholder-white/30 resize-none focus:outline-none focus:border-rose-400/40" />

              {error && <p className="text-rose-400 text-sm mt-3 font-['Manrope']" data-testid="kiosk-mp-error">{error}</p>}

              <button onClick={submitMissedPunch} disabled={submitting}
                data-testid="kiosk-mp-submit"
                className="w-full mt-6 h-14 rounded-2xl bg-white text-slate-900 font-['Outfit'] font-semibold text-lg hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                {submitting ? "Sending…" : "Submit Request"}
              </button>
            </div>
          </div>
        )}

        {/* Stage: REQUEST_CONFIRM (auto-returns after 4s) */}
        {stage === "request_confirm" && result && (
          <div className="max-w-lg mx-auto mt-10" data-testid="kiosk-request-confirm">
            <div className="rounded-[32px] p-10 text-center relative overflow-hidden bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-400/30">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 shadow-emerald-500/40 shadow-2xl flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="font-['Outfit'] text-4xl font-semibold text-white tracking-tight mb-2">
                Got it, {result.user_name?.split(" ")[0] || "thanks"}!
              </h2>
              <p className="text-white/70 font-['Manrope'] text-lg mb-6">
                Your {result.kind === "time_off" ? "time-off request" : "clock-in fix"} is in the queue.
              </p>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-5 inline-flex items-center gap-2">
                <CalIcon className="w-4 h-4 text-emerald-200" />
                <p className="text-white/90 font-['Manrope'] text-sm">A manager will review it shortly.</p>
              </div>
              <p className="text-white/30 text-xs font-['Manrope'] mt-8 flex items-center justify-center gap-1">
                <Check className="w-3 h-3" />
                Returning to the roster in a few seconds…
              </p>
            </div>
          </div>
        )}

        {/* Stage: CONFIRMATION */}
        {stage === "confirm" && result && (
          <div className="max-w-lg mx-auto mt-10" data-testid="kiosk-confirm">
            {(() => {
              // Per-action visual theming. Break events get their own
              // amber palette so they're instantly distinct from clock_in
              // (emerald) and clock_out (rose).
              const isClockIn = result.action === "clock_in";
              const isClockOut = result.action === "clock_out";
              const isBreakStart = result.action === "break_start";
              const isBreakEnd = result.action === "break_end";
              const palette = isClockIn ? "from-emerald-500/20 to-teal-500/20 border-emerald-400/30"
                : isClockOut ? "from-rose-500/20 to-violet-500/20 border-rose-400/30"
                : isBreakStart ? "from-amber-500/20 to-orange-500/20 border-amber-400/30"
                : "from-sky-500/20 to-emerald-500/20 border-sky-400/30";
              const iconBg = isClockIn ? "bg-emerald-500 shadow-emerald-500/40"
                : isClockOut ? "bg-rose-500 shadow-rose-500/40"
                : isBreakStart ? "bg-amber-500 shadow-amber-500/40"
                : "bg-sky-500 shadow-sky-500/40";
              const Icon = isClockIn ? LogIn : isClockOut ? LogOut : Clock;
              const title = isClockIn ? `Welcome, ${result.user_name?.split(" ")[0] || ""}!`
                : isClockOut ? `Nice shift, ${result.user_name?.split(" ")[0] || ""}!`
                : isBreakStart ? `Enjoy your break, ${result.user_name?.split(" ")[0] || ""}.`
                : `Welcome back, ${result.user_name?.split(" ")[0] || ""}!`;
              const subtitle = isClockIn ? "You're clocked in"
                : isClockOut ? "You're clocked out"
                : isBreakStart ? "You're on break"
                : "Break ended";
              const timeLabel = isClockIn ? "Started at"
                : isClockOut ? "Ended at"
                : isBreakStart ? "Break started"
                : "Break ended";
              return (
                <div className={`rounded-[32px] p-10 text-center relative overflow-hidden bg-gradient-to-br ${palette} border`}>
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${iconBg} shadow-2xl`}>
                    <Icon className="w-10 h-10 text-white" strokeWidth={2} />
                  </div>
                  <h2 className="font-['Outfit'] text-4xl font-semibold text-white tracking-tight mb-2" data-testid="kiosk-confirm-title">
                    {title}
                  </h2>
                  <p className="text-white/70 font-['Manrope'] text-lg mb-6">{subtitle}</p>
                  <div className="bg-white/10 backdrop-blur rounded-2xl p-5 inline-flex flex-col items-center">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 font-['Manrope']">{timeLabel}</p>
                    <p className="text-white font-['Outfit'] text-2xl font-semibold tabular-nums mt-1" data-testid="kiosk-confirm-time">
                      {formatTime(result.time, tenantTz)}
                    </p>
                    {isClockOut && (
                      <p className="text-emerald-300 font-['Manrope'] text-sm font-semibold mt-2 flex items-center gap-1" data-testid="kiosk-confirm-duration">
                        <Clock className="w-3.5 h-3.5" />
                        Worked {formatHoursMinutes(result.duration_hours)}
                      </p>
                    )}
                    {isBreakEnd && result.duration_hours != null && (
                      <p className="text-sky-300 font-['Manrope'] text-sm font-semibold mt-2 flex items-center gap-1" data-testid="kiosk-confirm-break-duration">
                        <Clock className="w-3.5 h-3.5" />
                        Break length {formatHoursMinutes(result.duration_hours)}
                      </p>
                    )}
                    {result.location_name && (
                      <p className="text-white/60 font-['Manrope'] text-xs mt-2 flex items-center gap-1" data-testid="kiosk-confirm-location">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300" />
                        at {result.location_name}
                      </p>
                    )}
                  </div>
                  {/* Time-off decision notifications. Approved → emerald
                      card with reviewer note; rejected → rose card.
                      Auto-acks on timeout via the effect above. */}
                  {(result.time_off_notifications || []).length > 0 && (
                    <div className="mt-6 space-y-3" data-testid="kiosk-time-off-notifications">
                      {result.time_off_notifications.map((n) => {
                        const approved = n.status === "approved";
                        const reqTypeLabel = (n.type || "vacation").replace(/_/g, " ");
                        const dateRange = n.start_date === n.end_date
                          ? n.start_date
                          : `${n.start_date} → ${n.end_date}`;
                        return (
                          <div
                            key={n.request_id}
                            className={`rounded-2xl p-4 text-left ring-1 backdrop-blur ${
                              approved
                                ? "bg-emerald-500/15 ring-emerald-400/40"
                                : "bg-rose-500/15 ring-rose-400/40"
                            }`}
                            data-testid={`kiosk-time-off-${n.request_id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                approved ? "bg-emerald-500" : "bg-rose-500"
                              }`}>
                                {approved
                                  ? <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
                                  : <X className="w-5 h-5 text-white" strokeWidth={2.5} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-['Outfit'] text-lg font-semibold ${
                                  approved ? "text-emerald-200" : "text-rose-200"
                                }`}>
                                  Time-off {approved ? "approved" : "denied"}
                                </p>
                                <p className="text-white/80 text-sm font-['Manrope'] mt-0.5 capitalize">
                                  {reqTypeLabel} · {dateRange}
                                  {n.total_days ? ` · ${n.total_days} day${n.total_days === 1 ? "" : "s"}` : ""}
                                </p>
                                {n.reviewer_note && (
                                  <p className="text-white/70 text-xs font-['Manrope'] mt-2 italic">
                                    "{n.reviewer_note}"
                                    {n.reviewer_name && <span className="text-white/40 not-italic"> — {n.reviewer_name}</span>}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-white/30 text-xs font-['Manrope'] mt-8 flex items-center justify-center gap-1">
                    <Check className="w-3 h-3" />
                    Returning to the roster in a few seconds…
                  </p>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Unpair modal */}
      {unpairOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="kiosk-unpair-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-7 max-w-sm w-full mx-6 relative">
            <button onClick={() => { setUnpairOpen(false); setUnpairInput(""); setUnpairError(""); }}
              className="absolute top-4 right-4 text-white/40 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-rose-400" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-1">Unpair this device?</h3>
              <p className="text-white/50 text-sm font-['Manrope'] mb-5 leading-relaxed">
                Enter the current 6-digit pairing code to confirm. You'll need to re-pair with the latest code next time.
              </p>
              <input
                type="tel"
                inputMode="numeric"
                autoFocus
                value={unpairInput}
                onChange={e => { setUnpairError(""); setUnpairInput(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
                placeholder="000000"
                data-testid="kiosk-unpair-input"
                className="w-full h-14 text-center bg-white/10 border border-white/10 rounded-2xl text-white text-2xl tracking-[0.4em] font-mono font-semibold placeholder-white/20 focus:outline-none focus:border-rose-400/40 tabular-nums"
              />
              {unpairError && <p className="text-rose-400 text-xs mt-2 font-['Manrope']">{unpairError}</p>}
              <button onClick={handleUnpair}
                disabled={unpairInput.length !== 6}
                data-testid="kiosk-unpair-confirm"
                className="w-full mt-5 h-11 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold text-sm disabled:opacity-30 disabled:cursor-not-allowed">
                Unpair Device
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
